/**
 * A small, safe subset of Markdown for chat messages. The parser produces an
 * AST that React renders as elements — user content is never injected as HTML.
 *
 * Supported: ```code blocks```, `inline code`, **bold**, *italic* / _italic_,
 * ~~strike~~, ||spoilers||, > quotes, http(s) links, <@userId> mentions,
 * <@&roleId> role mentions, @everyone, :emoji: and <:name:id> custom server emoji.
 */

export type MdNode =
  | { type: 'text'; value: string }
  | { type: 'bold' | 'italic' | 'strike' | 'spoiler'; children: MdNode[] }
  | { type: 'code'; value: string }
  | { type: 'codeblock'; lang: string | null; value: string }
  | { type: 'quote'; children: MdNode[] }
  | { type: 'link'; href: string }
  | { type: 'mention'; userId: string }
  | { type: 'roleMention'; roleId: string }
  | { type: 'everyone' }
  | { type: 'emoji'; value: string; name: string }
  | { type: 'customEmoji'; id: string; name: string };

export type EmojiResolver = (shortcode: string) => string | undefined;

const INLINE_RE = new RegExp(
  [
    '(`+)([\\s\\S]*?[^`])\\1(?!`)', // 1,2 inline code
    '<@([a-z0-9]{8,40})>', // 3 mention
    '<(https?:\\/\\/[^\\s<>"]+)>', // 4 <link>: shown without brackets, never previewed
    '(https?:\\/\\/[^\\s<>"]+)', // 5 link
    '\\*\\*([\\s\\S]+?)\\*\\*', // 6 bold
    '~~([\\s\\S]+?)~~', // 7 strike
    '\\|\\|([\\s\\S]+?)\\|\\|', // 8 spoiler
    '\\*(?![\\s*])([\\s\\S]*?[^\\s*])\\*', // 9 italic *
    '(?<![A-Za-z0-9_])_(?![\\s_])([\\s\\S]*?[^\\s_])_(?![A-Za-z0-9_])', // 10 italic _
    ':([a-z0-9_+\\-]{2,32}):', // 11 emoji shortcode
    '<@&([a-z0-9]{8,40})>', // 12 role mention
    '<a?:([A-Za-z0-9_]{2,32}):([a-z0-9]{8,40})>', // 13,14 custom emoji
    '(?<![^\\s(\\[{"\'*_~|>])(@everyone)(?![A-Za-z0-9_])', // 15 @everyone (bounded as in mentionsEveryone)
  ].join('|'),
  'g',
);

/** Trailing punctuation usually belongs to the sentence, not the URL. */
export function splitLink(url: string): [string, string] {
  let end = url.length;
  while (end > 0 && /[.,!?;:'"\])}]/.test(url[end - 1]!)) {
    // Keep a closing paren if the URL contains a matching opening one.
    if (url[end - 1] === ')' && (url.slice(0, end).match(/\(/g)?.length ?? 0) >= (url.slice(0, end).match(/\)/g)?.length ?? 0)) break;
    end--;
  }
  return [url.slice(0, end), url.slice(end)];
}

function pushText(nodes: MdNode[], value: string) {
  if (!value) return;
  const last = nodes.at(-1);
  if (last?.type === 'text') last.value += value;
  else nodes.push({ type: 'text', value });
}

export function parseInline(input: string, resolveEmoji?: EmojiResolver, depth = 0): MdNode[] {
  const nodes: MdNode[] = [];
  if (depth > 6) {
    pushText(nodes, input);
    return nodes;
  }
  const re = new RegExp(INLINE_RE.source, 'g');
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(input))) {
    pushText(nodes, input.slice(last, match.index));
    last = re.lastIndex;
    const [full, , code, mention, bracketLink, link, bold, strike, spoiler, italicStar, italicUnderscore, emoji, roleMention, customName, customId, everyone] = match;
    if (everyone) nodes.push({ type: 'everyone' });
    else if (code !== undefined) nodes.push({ type: 'code', value: code.replace(/^ (.+) $/, '$1') });
    else if (mention) nodes.push({ type: 'mention', userId: mention });
    else if (roleMention) nodes.push({ type: 'roleMention', roleId: roleMention });
    else if (customName && customId) nodes.push({ type: 'customEmoji', id: customId, name: customName });
    else if (bracketLink) nodes.push({ type: 'link', href: bracketLink });
    else if (link) {
      const [href, rest] = splitLink(link);
      nodes.push({ type: 'link', href });
      pushText(nodes, rest);
    } else if (bold !== undefined) nodes.push({ type: 'bold', children: parseInline(bold, resolveEmoji, depth + 1) });
    else if (strike !== undefined) nodes.push({ type: 'strike', children: parseInline(strike, resolveEmoji, depth + 1) });
    else if (spoiler !== undefined) nodes.push({ type: 'spoiler', children: parseInline(spoiler, resolveEmoji, depth + 1) });
    else if (italicStar !== undefined) nodes.push({ type: 'italic', children: parseInline(italicStar, resolveEmoji, depth + 1) });
    else if (italicUnderscore !== undefined) nodes.push({ type: 'italic', children: parseInline(italicUnderscore, resolveEmoji, depth + 1) });
    else if (emoji !== undefined) {
      const value = resolveEmoji?.(emoji);
      if (value) nodes.push({ type: 'emoji', value, name: emoji });
      else pushText(nodes, full);
    } else pushText(nodes, full);
  }
  pushText(nodes, input.slice(last));
  return nodes;
}

/** Parses block structure (code fences and quotes), then inline formatting. */
export function parseMarkdown(input: string, resolveEmoji?: EmojiResolver): MdNode[] {
  const nodes: MdNode[] = [];
  const fence = /```(?:([a-zA-Z0-9_+\-]{1,20})\n)?([\s\S]*?)```/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = fence.exec(input))) {
    nodes.push(...parseQuotes(input.slice(last, match.index), resolveEmoji));
    nodes.push({ type: 'codeblock', lang: match[1] ?? null, value: match[2]!.replace(/^\n|\n$/g, '') });
    last = fence.lastIndex;
  }
  nodes.push(...parseQuotes(input.slice(last), resolveEmoji));
  return nodes;
}

function parseQuotes(text: string, resolveEmoji?: EmojiResolver): MdNode[] {
  if (!text) return [];
  const out: MdNode[] = [];
  const lines = text.split('\n');
  let buffer: string[] = [];
  let quote: string[] = [];
  const flushText = () => {
    if (buffer.length) out.push(...parseInline(buffer.join('\n'), resolveEmoji));
    buffer = [];
  };
  const flushQuote = () => {
    if (quote.length) out.push({ type: 'quote', children: parseInline(quote.join('\n'), resolveEmoji) });
    quote = [];
  };
  for (const line of lines) {
    if (/^> /.test(line) || line === '>') {
      flushText();
      quote.push(line.replace(/^> ?/, ''));
    } else {
      flushQuote();
      buffer.push(line);
    }
  }
  flushQuote();
  flushText();
  return out;
}

/** True when a message is only 1–3 emoji (rendered larger, like most chat apps). */
export function isEmojiOnly(nodes: MdNode[]): boolean {
  let count = 0;
  for (const node of nodes) {
    if (node.type === 'emoji' || node.type === 'customEmoji') count++;
    else if (node.type === 'text') {
      const stripped = node.value.replace(/\s+/g, '');
      if (!stripped) continue;
      const emoji = stripped.match(/\p{Extended_Pictographic}(‍\p{Extended_Pictographic}|️|\p{Emoji_Modifier})*/gu);
      if (!emoji || emoji.join('') !== stripped) return false;
      count += emoji.length;
    } else return false;
  }
  return count > 0 && count <= 3;
}
